import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = '30ds_cookie_consent_v1';

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...choice, essential: true,
        decided_at: new Date().toISOString(), version: 1,
      }));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent-banner" className="fixed bottom-0 inset-x-0 z-[60] px-4 pb-4 pointer-events-none">
      <div className="max-w-4xl mx-auto pointer-events-auto rounded-2xl border border-zinc-700 bg-zinc-950/95 backdrop-blur-md shadow-2xl">
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-10 h-10 rounded-full bg-amber-500/10 items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm mb-1">Cookies on 30 Day Skills</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                We use essential cookies to keep you signed in and run the site. With your
                consent we also use anonymous analytics to improve it. Read our{' '}
                <Link to="/privacy" className="text-amber-300 underline underline-offset-2 hover:text-amber-200">Privacy Policy</Link>.
              </p>
              {showDetails && (
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800">
                    <div>
                      <div className="text-white font-medium">Essential</div>
                      <div className="text-zinc-500 text-xs">Required for sign-in, sessions, and checkout.</div>
                    </div>
                    <span className="text-xs text-zinc-500 mt-0.5 whitespace-nowrap">Always on</span>
                  </div>
                  <label className="flex items-start justify-between gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800 cursor-pointer" data-testid="cookie-analytics-toggle">
                    <div>
                      <div className="text-white font-medium">Analytics (anonymous)</div>
                      <div className="text-zinc-500 text-xs">Aggregated page views and feature usage.</div>
                    </div>
                    <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1 h-4 w-4 accent-amber-400" />
                  </label>
                </div>
              )}
            </div>
            <button type="button" aria-label="Dismiss" onClick={() => persist({ analytics: false })} data-testid="cookie-dismiss-btn" className="text-zinc-500 hover:text-zinc-300">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => persist({ analytics: true })} data-testid="cookie-accept-all-btn" className="px-4 py-2 rounded-lg bg-amber-400 text-zinc-950 text-sm font-semibold hover:bg-amber-300">Accept all</button>
            <button type="button" onClick={() => persist({ analytics: false })} data-testid="cookie-reject-btn" className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-200 text-sm font-medium hover:bg-zinc-800">Reject non-essential</button>
            {showDetails ? (
              <button type="button" onClick={() => persist({ analytics })} data-testid="cookie-save-prefs-btn" className="px-4 py-2 rounded-lg border border-amber-700/60 text-amber-200 text-sm font-medium hover:bg-amber-950/30">Save preferences</button>
            ) : (
              <button type="button" onClick={() => setShowDetails(true)} data-testid="cookie-customise-btn" className="px-4 py-2 text-zinc-400 text-sm font-medium hover:text-zinc-200">Customise</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
